/**
 * AI Service Shim — bridges `window.spark.llm()` calls to the backend
 * OpenRouter proxy at `/api/ai/chat` so AI features work on Railway
 * (outside the GitHub Spark runtime).
 *
 * Loaded before `@github/spark/spark` in main.tsx so it can intercept
 * llm calls when the native Spark SDK isn't available.
 *
 * When running inside Spark, the native SDK takes priority and this
 * module is a no-op.
 */

const AI_PROXY_PATH = "/api/ai/chat";
const KV_STORAGE_PREFIX = "bba:spark-kv:";
const KV_EVENT_NAME = "bba:spark-kv-updated";

type SparkKvValue = unknown;

function canUseLocalStorage(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const probeKey = `${KV_STORAGE_PREFIX}__probe__`;
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

function emitKvEvent(key: string, value?: SparkKvValue): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(KV_EVENT_NAME, { detail: { key, value } }),
  );
}

function createKvFallback() {
  const hasStorage = canUseLocalStorage();

  const readValue = <T>(key: string): T | undefined => {
    if (!hasStorage) return undefined;
    const raw = window.localStorage.getItem(`${KV_STORAGE_PREFIX}${key}`);
    if (raw == null) return undefined;
    return JSON.parse(raw) as T;
  };

  return {
    async keys(): Promise<string[]> {
      if (!hasStorage) return [];
      const keys: string[] = [];
      for (let index = 0; index < window.localStorage.length; index += 1) {
        const storageKey = window.localStorage.key(index);
        if (!storageKey?.startsWith(KV_STORAGE_PREFIX)) continue;
        keys.push(storageKey.slice(KV_STORAGE_PREFIX.length));
      }
      return keys;
    },
    async get<T>(key: string): Promise<T | undefined> {
      try {
        return readValue<T>(key);
      } catch {
        return undefined;
      }
    },
    async set<T>(key: string, value: T): Promise<void> {
      if (!hasStorage) return;
      window.localStorage.setItem(
        `${KV_STORAGE_PREFIX}${key}`,
        JSON.stringify(value),
      );
      emitKvEvent(key, value);
    },
    async delete(key: string): Promise<void> {
      if (!hasStorage) return;
      window.localStorage.removeItem(`${KV_STORAGE_PREFIX}${key}`);
      emitKvEvent(key, undefined);
    },
  };
}

/**
 * Tagged template helper — identical to Spark's `llmPrompt`.
 * Joins template literal parts and interpolations into a single string.
 */
function llmPrompt(
  strings: TemplateStringsArray,
  ...values: unknown[]
): string {
  let result = "";
  for (let i = 0; i < strings.length; i++) {
    result += strings[i];
    if (i < values.length) {
      result += String(values[i]);
    }
  }
  return result;
}

/**
 * Call the backend AI proxy.
 * Matches the Spark SDK signature: `llm(prompt, modelName?, jsonMode?)`
 */
async function llm(
  prompt: string,
  _modelName?: string,
  jsonMode?: boolean,
): Promise<string> {
  const body: Record<string, unknown> = { prompt };
  if (jsonMode) {
    body.jsonMode = true;
  }

  const res = await fetch(AI_PROXY_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ message: `AI proxy returned ${res.status}` }));
    throw new Error(err.message || `AI request failed (${res.status})`);
  }

  const data = await res.json();
  return data.text as string;
}

/**
 * Install the shim on `window.spark` if:
 *  1. The native Spark SDK didn't already define `window.spark.llm`, OR
 *  2. We're in a production environment where Spark auth isn't available.
 *
 * This preserves any existing `kv` or other properties already on `window.spark`.
 */
export function installAiShim(): void {
  if (typeof window === "undefined") return;

  // Ensure the spark namespace exists
  if (!window.spark) {
    (window as unknown as { spark: unknown }).spark = {};
  }

  if (!window.spark.kv || typeof window.spark.kv.get !== "function") {
    (
      window.spark as unknown as { kv: ReturnType<typeof createKvFallback> }
    ).kv = createKvFallback();
  }

  // Only override if native llm is absent or non-functional
  // (Spark SDK stubs may exist but throw when not authenticated)
  const needsShim =
    !window.spark.llm ||
    typeof window.spark.llm !== "function" ||
    // If we're on Railway (not Spark host), always use our proxy
    (typeof location !== "undefined" && !location.hostname.includes("spark"));

  if (needsShim) {
    (window.spark as unknown as { llm: typeof llm }).llm = llm;
    (window.spark as unknown as { llmPrompt: typeof llmPrompt }).llmPrompt =
      llmPrompt;
    console.log(
      "[ai-shim] Installed OpenRouter proxy shim for window.spark.llm",
    );
  }
}
